import React, { useState, useEffect, useCallback } from 'react';
import { useSuperAdmin } from '../../contexts/SuperAdminContext';
import { Link } from 'react-router-dom';
import SALayout from './SALayout';
import { Card, CardContent } from '../../components/ui/card';
import { Building2, Users, CreditCard, Clock, Activity, Tag, ArrowRight, Loader2, IndianRupee } from 'lucide-react';

export default function SADashboard() {
  const { apiFetch } = useSuperAdmin();
  const [stats, setStats] = useState(null);
  const [recentOrgs, setRecentOrgs] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, orgsRes] = await Promise.all([
        apiFetch('/stats'),
        apiFetch('/organizations')
      ]);
      if (statsRes.ok) setStats(await statsRes.json());
      if (orgsRes.ok) {
        const orgs = await orgsRes.json();
        setRecentOrgs(orgs.slice(0, 8));
      }
    } catch {} finally {
      setLoading(false);
    }
  }, [apiFetch]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const formatCurrency = (val) => {
    if (val >= 100000) return `₹${(val / 100000).toFixed(1)}L`;
    if (val >= 1000) return `₹${(val / 1000).toFixed(1)}K`;
    return `₹${val}`;
  };

  const STAT_CARDS = stats ? [
    { label: 'Total Revenue', value: formatCurrency(stats.total_revenue || 0), icon: IndianRupee, color: 'text-green-600 bg-green-50' },
    { label: 'Organizations', value: stats.total_organizations, icon: Building2, color: 'text-blue-600 bg-blue-50' },
    { label: 'Total Users', value: stats.total_users, icon: Users, color: 'text-emerald-600 bg-emerald-50' },
    { label: 'Active Subs', value: stats.active_subscriptions, icon: CreditCard, color: 'text-purple-600 bg-purple-50' },
    { label: 'On Trial', value: stats.trial_subscriptions, icon: Clock, color: 'text-amber-600 bg-amber-50' },
    { label: 'Active Today', value: stats.active_users_today, icon: Activity, color: 'text-cyan-600 bg-cyan-50' },
    { label: 'Promo Used', value: stats.promo_redemptions, icon: Tag, color: 'text-rose-600 bg-rose-50' },
  ] : [];

  const getPlanBadge = (plan) => {
    const styles = {
      active: 'bg-emerald-100 text-emerald-700',
      trial: 'bg-amber-100 text-amber-700',
      expired: 'bg-red-100 text-red-700',
      cancelled: 'bg-slate-100 text-slate-600',
      free: 'bg-slate-100 text-slate-500'
    };
    return <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${styles[plan] || styles.free}`}>{plan}</span>;
  };

  if (loading) {
    return <SALayout><div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div></SALayout>;
  }

  return (
    <SALayout>
      <div className="space-y-6" data-testid="sa-dashboard">
        <h1 className="text-xl font-bold text-slate-900">Dashboard</h1>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {STAT_CARDS.map(s => (
            <Card key={s.label} className="border-slate-200/60">
              <CardContent className="p-4">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-2 ${s.color}`}>
                  <s.icon className="w-4 h-4" />
                </div>
                <p className="text-2xl font-bold text-slate-900">{s.value}</p>
                <p className="text-xs text-slate-500 mt-0.5">{s.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Recent Orgs */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-slate-900">Recent Organizations</h2>
            <Link to="/superadmin/organizations" className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1" data-testid="sa-view-all-orgs">
              View all <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="bg-white rounded-xl border border-slate-200/60 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/50">
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-slate-500">Organization</th>
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-slate-500">Plan</th>
                  <th className="text-center px-4 py-2.5 text-xs font-medium text-slate-500">Members</th>
                  <th className="text-right px-4 py-2.5 text-xs font-medium text-slate-500">Total Spend</th>
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-slate-500">Created</th>
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-slate-500">Promo</th>
                </tr>
              </thead>
              <tbody>
                {recentOrgs.map(org => (
                  <tr key={org.id} className="border-b border-slate-50 hover:bg-slate-50/50 cursor-pointer" onClick={() => window.location.href = `/superadmin/organizations/${org.id}`}>
                    <td className="px-4 py-3">
                      <span className="font-medium text-slate-900 text-xs">{org.name}</span>
                    </td>
                    <td className="px-4 py-3">{getPlanBadge(org.plan)}</td>
                    <td className="px-4 py-3 text-center text-xs text-slate-600">{org.member_count}/{org.seat_limit}</td>
                    <td className="px-4 py-3 text-right text-xs font-medium text-slate-700" data-testid={`sa-dash-org-spend-${org.id}`}>
                      {org.total_spend > 0 ? `₹${org.total_spend.toLocaleString('en-IN')}` : <span className="text-slate-300">-</span>}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500">{new Date(org.created_at).toLocaleDateString()}</td>
                    <td className="px-4 py-3 text-xs">{org.promo_code ? <span className="text-purple-600 font-medium">{org.promo_code}</span> : <span className="text-slate-300">-</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </SALayout>
  );
}
